import React, { useState } from 'react'
import { Button } from '@/components/_ui/Button'
import T from '@/components/Helpers/Translate'
import { Input } from '@/components/_ui/Input'
import { Skeleton } from '@/components/_ui/Skeleton'
import { DialogClose } from '@radix-ui/react-dialog'
import useGlobalStore from '@/store/useGlobalStore'
import type { HydraGetShipToOption } from '@/lib/services/Hydra/shipToOptions/hydraGetShipToOptions'

const LocationLoadingSkeleton = () => (
  <div>
    <Skeleton className="my-4 h-[64px] rounded-md border border-[#99BCDF66] px-6 py-4" />
    <Skeleton className="my-4 h-[64px] rounded-md border border-[#99BCDF66] px-6 py-4" />
    <Skeleton className="my-4 h-[64px] rounded-md border border-[#99BCDF66] px-6 py-4" />
  </div>
)

export const LocationChangeForm = ({
  handleConfirmForm,
  shipToOptions,
  isLoadingShipToOptions,
}: {
  shipToOptions: HydraGetShipToOption[] | null
  isLoadingShipToOptions: boolean
  handleConfirmForm: (selectedAddress: number) => void
}) => {
  const [shipItemsLimit, setShipItemsLimit] = useState(3)
  const [activeLocation, setActiveLocation] = useState<number>(0)
  const [zipCode, setZipCode] = useState('')
  const [isZipCodeApplied, setIsZipCodeApplied] = useState(false)
  const {
    user: { isSignedIn },
  } = useGlobalStore()

  const handleClickDone = () => {
    setActiveLocation(activeLocation)
    handleConfirmForm(activeLocation)
  }

  const chooseLocationSection = () => (
    <div>
      {isLoadingShipToOptions && <LocationLoadingSkeleton />}
      {!isLoadingShipToOptions && (
        <div>
          {shipToOptions
            ?.slice(0, shipItemsLimit)
            .map((shipToOption, index) => (
              <div
                role="button"
                tabIndex={0}
                key={`ship-option-${shipToOption.shipToOptionId.unicity}/${shipToOption.shipToName.firstName}/${shipToOption.shipToName.lastName}`}
                className={`relative my-4 rounded-md border border-[#99BCDF66] px-6 py-4 text-primary hover:bg-accent ${
                  activeLocation === index &&
                  'border-[#99BCDF border-2 bg-[#99BCDF1A]'
                }`}
                onClick={() => {
                  setActiveLocation(index)
                  setIsZipCodeApplied(false)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveLocation(index)
                    setIsZipCodeApplied(false)
                  }
                }}
              >
                <div>
                  <div className="text-sm font-semibold">
                    {`${shipToOption.shipToName.firstName} ${shipToOption.shipToName.lastName}`}
                  </div>
                  <div className="flex flex-col md:flex-row">
                    <span>{`${shipToOption.shipToAddress.address1},\u00A0`}</span>
                    <span>{`${shipToOption.shipToAddress.city}, ${shipToOption.shipToAddress.zip}`}</span>
                  </div>
                </div>
                {shipToOption.isDefaultOption && (
                  <div className="absolute top-1/3 right-5 rounded border border-primary px-[6px] py-[3px] text-[9px] leading-[10px] font-bold uppercase">
                    <T>default</T>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      <div className="flex w-full justify-end">
        {shipItemsLimit !== shipToOptions?.length &&
          shipToOptions &&
          shipToOptions?.length > 0 && (
            <Button
              variant="link"
              className="text-sm font-normal underline"
              onClick={() => setShipItemsLimit(shipToOptions?.length || 0)}
            >
              <T>see_all</T>
            </Button>
          )}
      </div>
    </div>
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const zipCodeSection = () => (
    <div className="mt-6">
      {isSignedIn && (
        <span className="text-lg leading-none font-semibold tracking-tight text-primary">
          or_new_zip_code
        </span>
      )}
      {!isZipCodeApplied && (
        <div className="mt-6 flex flex-row gap-4">
          <Input
            onChange={(event) => setZipCode(event.target.value)}
            className="max-h-10"
          />
          <Button
            variant="secondary"
            className="min-w-32 rounded"
            disabled={zipCode.length <= 0}
            onClick={() => {
              setActiveLocation(-1)
              setIsZipCodeApplied(true)
            }}
          >
            <T>apply</T>
          </Button>
        </div>
      )}
      {isZipCodeApplied && (
        <div className="border-[#99BCDF relative my-4 rounded-md border-2 border-[#99BCDF66] bg-[#99BCDF1A] px-6 py-3 text-primary hover:bg-accent">
          <div className="flex flex-row gap-1 text-sm">
            <span>
              <T>deliver_to</T>
            </span>
            <span className="font-semibold">{zipCode}</span>
            <span>-</span>
            <span className="cursor-pointer underline">
              <Button
                variant="link"
                className="m-0 p-0 text-sm font-normal underline"
                size="xs"
                onClick={() => {
                  setIsZipCodeApplied(false)
                  setZipCode('')
                }}
              >
                <T>change</T>
              </Button>
            </span>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div>
      {isSignedIn && chooseLocationSection()}
      {/* {zipCodeSection()} */}

      <div className="mt-16 flex justify-between">
        <DialogClose>
          <Button className="p-0 underline" variant="link">
            <T>Close</T>
          </Button>
        </DialogClose>

        <Button
          variant="secondary"
          className="w-[195px] rounded-full"
          onClick={handleClickDone}
        >
          <T>save</T>
        </Button>
      </div>
    </div>
  )
}
